import React, { useRef, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Bold, Italic, List, ListOrdered, Link, Code, Quote, Minus, Heading1, Heading2, Heading3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import TurndownService from 'turndown';
import { marked } from 'marked';

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  height?: number | string;
  className?: string;
  disabled?: boolean;
  isEditable?: boolean;
}

// Ref type for external access to editor methods
export interface MarkdownEditorRef {
  getMarkdown: () => string;
}

interface ToolbarItem {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  command: string;
  value?: string;
}

// Initialize Turndown for HTML to Markdown conversion
const turndownService = new TurndownService({
  headingStyle: 'atx',
  codeBlockStyle: 'fenced',
  bulletListMarker: '-',
  emDelimiter: '*',
  strongDelimiter: '**',
  linkStyle: 'inlined',
});

// Configure Turndown to preserve more formatting
turndownService.keep(['mark', 'ins']);
turndownService.addRule('strikethrough', {
  filter: ['del', 's'],
  replacement: (content) => `~~${content}~~`
});

// Preserve manual line breaks so spacing isn't lost on round-trips
turndownService.addRule('lineBreak', {
  filter: 'br',
  replacement: () => '\n'
});

// Treat bare divs as block breaks so structure isn't flattened
turndownService.addRule('divBlock', {
  filter: (node) => node.nodeName === 'DIV',
  replacement: (content) => `\n\n${content.trim()}\n\n`
});

// Add custom table rules
turndownService.addRule('tableCell', {
  filter: ['th', 'td'],
  replacement: function (content) {
    const cleanContent = content.replace(/\n/g, ' ').trim();
    return ` ${cleanContent} |`;
  }
});

turndownService.addRule('tableRow', {
  filter: 'tr',
  replacement: function (content, node) {
    const element = node as HTMLTableRowElement;
    const cells = content.trim();
    
    const isHeader = element.parentElement?.tagName === 'THEAD' || 
                     element.querySelectorAll('th').length > 0;
    
    let row = '|' + cells;
    
    if (isHeader) {
      const cellCount = element.cells.length;
      const separator = '\n|' + Array(cellCount).fill(' --- ').join('|') + '|';
      row += separator;
    }
    
    return row + '\n';
  }
});

turndownService.addRule('table', {
  filter: 'table',
  replacement: function (content) {
    const lines = content.trim().split('\n').filter(line => line.trim());
    return '\n\n' + lines.join('\n') + '\n\n';
  }
});

turndownService.addRule('tableSection', {
  filter: ['thead', 'tbody', 'tfoot'],
  replacement: function (content) {
    return content;
  }
});

// Configure marked
marked.setOptions({
  breaks: true,
  gfm: true,
});

// Markdown Preview Component - renders markdown as rich text
const MarkdownPreview: React.FC<{ content: string }> = ({ content }) => {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        h1: ({ children }) => (
          <h1 style={{ 
            fontSize: '1.75rem', 
            fontWeight: 700, 
            marginTop: '1.5rem', 
            marginBottom: '1rem',
            color: '#111827',
            fontFamily: 'Georgia, serif',
            lineHeight: 1.3
          }}>
            {children}
          </h1>
        ),
        h2: ({ children }) => (
          <h2 style={{ 
            fontSize: '1.375rem', 
            fontWeight: 600, 
            marginTop: '1.25rem', 
            marginBottom: '0.75rem',
            color: '#1f2937',
            fontFamily: 'Georgia, serif',
            borderBottom: '1px solid #e5e7eb',
            paddingBottom: '0.5rem',
            lineHeight: 1.3
          }}>
            {children}
          </h2>
        ),
        h3: ({ children }) => (
          <h3 style={{ 
            fontSize: '1.125rem', 
            fontWeight: 600, 
            marginTop: '1rem', 
            marginBottom: '0.5rem',
            color: '#374151',
            fontFamily: 'Georgia, serif',
            lineHeight: 1.4
          }}>
            {children}
          </h3>
        ),
        p: ({ children }) => (
          <p style={{ 
            marginTop: '0.75rem', 
            marginBottom: '0.75rem',
            color: '#374151',
            lineHeight: 1.7,
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
          }}>
            {children}
          </p>
        ),
        strong: ({ children }) => (
          <strong style={{ fontWeight: 600, color: '#111827' }}>
            {children}
          </strong>
        ),
        em: ({ children }) => (
          <em style={{ fontStyle: 'italic' }}>
            {children}
          </em>
        ),
        ul: ({ children }) => (
          <ul style={{ 
            marginTop: '0.75rem', 
            marginBottom: '0.75rem', 
            paddingLeft: '1.5rem',
            listStyleType: 'disc'
          }}>
            {children}
          </ul>
        ),
        ol: ({ children }) => (
          <ol style={{ 
            marginTop: '0.75rem', 
            marginBottom: '0.75rem', 
            paddingLeft: '1.5rem',
            listStyleType: 'decimal'
          }}>
            {children}
          </ol>
        ),
        li: ({ children }) => (
          <li style={{ 
            marginTop: '0.25rem', 
            marginBottom: '0.25rem',
            color: '#374151',
            lineHeight: 1.6
          }}>
            {children}
          </li>
        ),
        table: ({ children }) => (
          <div style={{ overflowX: 'auto', marginTop: '1rem', marginBottom: '1rem' }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              border: '1px solid #d1d5db',
              fontSize: '0.875rem'
            }}>
              {children}
            </table>
          </div>
        ),
        thead: ({ children }) => (
          <thead style={{ backgroundColor: '#f3f4f6' }}>
            {children}
          </thead>
        ),
        tbody: ({ children }) => (
          <tbody>
            {children}
          </tbody>
        ),
        tr: ({ children }) => (
          <tr style={{ borderBottom: '1px solid #d1d5db' }}>
            {children}
          </tr>
        ),
        th: ({ children }) => (
          <th style={{ 
            border: '1px solid #d1d5db',
            padding: '0.75rem',
            textAlign: 'left',
            fontWeight: 600,
            color: '#111827',
            backgroundColor: '#f9fafb'
          }}>
            {children}
          </th>
        ),
        td: ({ children }) => (
          <td style={{ 
            border: '1px solid #d1d5db',
            padding: '0.75rem',
            color: '#374151'
          }}>
            {children}
          </td>
        ),
        blockquote: ({ children }) => (
          <blockquote style={{ 
            borderLeft: '4px solid #d6001c',
            paddingLeft: '1rem',
            marginTop: '1rem',
            marginBottom: '1rem',
            fontStyle: 'italic',
            color: '#6b7280'
          }}>
            {children}
          </blockquote>
        ),
        code: ({ children, className }) => {
          const isBlock = className?.includes('language-');
          if (isBlock) {
            return (
              <code style={{
                display: 'block',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace',
                fontSize: '0.875rem'
              }}>
                {children}
              </code>
            );
          }
          return (
            <code style={{
              backgroundColor: '#f3f4f6',
              padding: '0.125rem 0.375rem',
              borderRadius: '0.25rem',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace',
              fontSize: '0.875rem',
              color: '#d6001c'
            }}>
              {children}
            </code>
          );
        },
        pre: ({ children }) => (
          <pre style={{
            backgroundColor: '#f3f4f6',
            padding: '1rem',
            borderRadius: '0.5rem',
            overflowX: 'auto',
            marginTop: '1rem',
            marginBottom: '1rem'
          }}>
            {children}
          </pre>
        ),
        hr: () => (
          <hr style={{ 
            marginTop: '1.5rem', 
            marginBottom: '1.5rem',
            border: 'none',
            borderTop: '1px solid #e5e7eb'
          }} />
        ),
        a: ({ href, children }) => (
          <a 
            href={href}
            style={{ color: '#d6001c', textDecoration: 'underline' }}
            target="_blank"
            rel="noopener noreferrer"
          >
            {children}
          </a>
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  );
};

export const MarkdownEditor = forwardRef<MarkdownEditorRef, MarkdownEditorProps>(({
  value,
  onChange,
  placeholder = 'Click to start writing...',
  height = 500,
  className,
  disabled = false,
  isEditable = false,
}, ref) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const isInternalUpdate = useRef(false);
  const lastMarkdown = useRef(value);
  const wasEditableRef = useRef(isEditable);
  const hasInitializedRef = useRef(false);
  const originalMarkdownRef = useRef(value); // Store original markdown for comparison
  const hasUserEditedRef = useRef(false); // Track if user actually made edits

  // Tools for the toolbar
  const tools: ToolbarItem[] = [
    { icon: Bold, label: 'Bold', command: 'bold' },
    { icon: Italic, label: 'Italic', command: 'italic' },
    { icon: Heading1, label: 'Heading 1', command: 'formatBlock', value: 'h1' },
    { icon: Heading2, label: 'Heading 2', command: 'formatBlock', value: 'h2' },
    { icon: Heading3, label: 'Heading 3', command: 'formatBlock', value: 'h3' },
    { icon: Code, label: 'Code', command: 'insertHTML', value: 'code' },
    { icon: Quote, label: 'Quote', command: 'formatBlock', value: 'blockquote' },
    { icon: List, label: 'Bullet List', command: 'insertUnorderedList' },
    { icon: ListOrdered, label: 'Numbered List', command: 'insertOrderedList' },
    { icon: Link, label: 'Link', command: 'createLink' },
    { icon: Minus, label: 'Divider', command: 'insertHorizontalRule' },
  ];

  // Convert markdown to HTML
  const markdownToHtml = useCallback((markdown: string): string => {
    if (!markdown) return '<p><br></p>';
    
    try {
      const html = marked.parse(markdown, { async: false }) as string;
      const temp = document.createElement('div');
      temp.innerHTML = html;
      
      const paragraphs = temp.querySelectorAll('p');
      paragraphs.forEach(p => {
        if (!p.textContent?.trim() && !p.querySelector('br')) {
          p.innerHTML = '<br>';
        }
      });
      
      return temp.innerHTML;
    } catch (error) {
      console.error('Error converting markdown to HTML:', error);
      let html = markdown
        .replace(/^### (.*?)$/gm, '<h3>$1</h3>')
        .replace(/^## (.*?)$/gm, '<h2>$1</h2>')
        .replace(/^# (.*?)$/gm, '<h1>$1</h1>')
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/^- (.*?)$/gm, '<li>$1</li>')
        .replace(/\n/g, '<br>');
      return `<p>${html}</p>`;
    }
  }, []);

  // Convert HTML to markdown
  const htmlToMarkdown = useCallback((element: HTMLElement): string => {
    if (!element || !element.innerHTML) return '';
    
    try {
      const temp = document.createElement('div');
      temp.innerHTML = element.innerHTML;
      
      // Normalize divs into paragraphs when they only contain inline content.
      // contentEditable often inserts <div> on Enter which Turndown treats as inline,
      // collapsing structure. Converting them to <p> preserves markdown block breaks.
      temp.querySelectorAll('div').forEach((div) => {
        const hasBlock = div.querySelector('p, ul, ol, table, h1, h2, h3, h4, h5, h6, blockquote, pre, hr');
        if (!hasBlock) {
          const p = document.createElement('p');
          p.innerHTML = div.innerHTML;
          div.replaceWith(p);
        }
      });

      const paragraphs = temp.querySelectorAll('p');
      paragraphs.forEach(p => {
        if (p.innerHTML === '<br>' || p.innerHTML === '<br/>') {
          p.innerHTML = '';
        }
      });
      
      let markdown = turndownService.turndown(temp.innerHTML);
      markdown = markdown.replace(/\n{3,}/g, '\n\n');
      markdown = markdown.trim();
      
      return markdown;
    } catch (error) {
      console.error('Error converting HTML to markdown:', error);
      return element.textContent || '';
    }
  }, []);

  // Expose getMarkdown method via ref for explicit save
  useImperativeHandle(ref, () => ({
    getMarkdown: () => {
      const originalMarkdown = originalMarkdownRef.current;

      // If user hasn't made any edits, return the original markdown to avoid
      // unnecessary HTML → Markdown conversions that can drop spacing.
      if (!hasUserEditedRef.current) {
        return originalMarkdown;
      }
      
      // User made edits, convert HTML to markdown
      if (editorRef.current) {
        const convertedMarkdown = htmlToMarkdown(editorRef.current);

        // If the conversion produced content identical to the original after
        // trimming inconsequential whitespace, prefer the original to prevent
        // formatting regressions when the user clicks Save without changes.
        const normalize = (value: string) =>
          value
            .replace(/\s+$/gm, '') // trim trailing whitespace on each line
            .trim();

        if (normalize(convertedMarkdown) === normalize(originalMarkdown)) {
          return originalMarkdown;
        }

        return convertedMarkdown;
      }
      return originalMarkdown;
    }
  }), [htmlToMarkdown]);

  // Execute formatting commands
  const executeCommand = useCallback((command: string, cmdValue?: string) => {
    if (!isEditable || !editorRef.current) return;

    editorRef.current.focus();
    hasUserEditedRef.current = true; // Mark as edited

    if (command === 'createLink') {
      const url = prompt('Enter URL:');
      if (url) {
        document.execCommand('createLink', false, url);
      }
    } else if (command === 'insertHTML' && cmdValue === 'code') {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const selectedText = range.toString() || 'code';
        const code = document.createElement('code');
        code.textContent = selectedText;
        range.deleteContents();
        range.insertNode(code);
        range.setStartAfter(code);
        range.setEndAfter(code);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    } else {
      document.execCommand(command, false, cmdValue);
    }
  }, [isEditable]);

  // Initialize editor content when switching to edit mode
  useEffect(() => {
    const wasEditable = wasEditableRef.current;
    const justSwitchedToEditable = isEditable && !wasEditable;
    
    wasEditableRef.current = isEditable;
    
    if (justSwitchedToEditable || (isEditable && !hasInitializedRef.current)) {
      // Store original markdown and reset edit tracking
      originalMarkdownRef.current = value;
      hasUserEditedRef.current = false;
      
      // Use requestAnimationFrame for faster initialization without visible delay
      const frameId = requestAnimationFrame(() => {
        if (editorRef.current) {
          isInternalUpdate.current = true;
          lastMarkdown.current = value;
          hasInitializedRef.current = true;
          
          const html = markdownToHtml(value || '');
          editorRef.current.innerHTML = html;
          
          if (window.getSelection && editorRef.current.lastChild) {
            const selection = window.getSelection();
            const range = document.createRange();
            range.selectNodeContents(editorRef.current);
            range.collapse(false);
            selection?.removeAllRanges();
            selection?.addRange(range);
          }
          
          isInternalUpdate.current = false;
        }
      });
      
      return () => cancelAnimationFrame(frameId);
    }
    
    if (!isEditable && wasEditable) {
      hasInitializedRef.current = false;
      hasUserEditedRef.current = false;
    }
  }, [isEditable, markdownToHtml, value]);


  // Handle paste - clean up pasted content
  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    hasUserEditedRef.current = true; // Mark as edited
    
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');
    
    if (html) {
      const temp = document.createElement('div');
      temp.innerHTML = html;
      
      // Remove style and class attributes to prevent formatting conflicts
      temp.querySelectorAll('*').forEach(el => {
        el.removeAttribute('style');
        el.removeAttribute('class');
      });
      
      document.execCommand('insertHTML', false, temp.innerHTML);
    } else if (text) {
      const htmlText = text.replace(/\n/g, '<br>');
      document.execCommand('insertHTML', false, htmlText);
    }
  }, []);

  // Track typing as edits
  const handleInput = useCallback(() => {
    if (isInternalUpdate.current) return; // Ignore programmatic updates
    hasUserEditedRef.current = true;

    // Keep parent informed of markdown changes when an onChange handler is provided
    if (editorRef.current) {
      const newMarkdown = htmlToMarkdown(editorRef.current);

      if (newMarkdown !== lastMarkdown.current) {
        lastMarkdown.current = newMarkdown;
        onChange(newMarkdown);
      }
    }
  }, [htmlToMarkdown, onChange]);

  return (
    <div className={cn("flex flex-col h-full border rounded-lg overflow-hidden bg-white dark:bg-gray-900", className)} style={{ minHeight: height }}>
      {/* Toolbar - Only show when editable */}
      {isEditable && (
        <div className="sticky top-0 z-20 bg-background border-b">
          <div className="flex items-center gap-1 p-2 bg-muted/50">
            {tools.map((tool) => {
              const Icon = tool.icon;
              return (
                <Button
                  key={tool.label}
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => executeCommand(tool.command, tool.value)}
                  disabled={disabled}
                  title={tool.label}
                  type="button"
                >
                  <Icon className="h-4 w-4" />
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {/* Content Area */}
      <div className="flex-1 overflow-auto">
        {isEditable ? (
          /* Edit Mode - contentEditable div with matching styles */
          <div
            ref={editorRef}
            contentEditable={!disabled}
            onInput={handleInput}
            onPaste={handlePaste}
            className="markdown-editor-content min-h-full w-full p-6 focus:outline-none"
            style={{ 
              minHeight: typeof height === 'number' ? `${height}px` : height
            }}
            suppressContentEditableWarning={true}
            data-placeholder={placeholder}
          />
        ) : (
          /* Preview Mode - Rendered Markdown */
          <div 
            style={{ 
              padding: '1.5rem',
              minHeight: typeof height === 'number' ? `${height}px` : height,
              backgroundColor: 'white'
            }}
          >
            {value ? (
              <MarkdownPreview content={value} />
            ) : (
              <p style={{ color: '#9ca3af', fontStyle: 'italic' }}>{placeholder}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

MarkdownEditor.displayName = 'MarkdownEditor';

export default MarkdownEditor;
